/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import DefaultCrudTable from '@/components/tables/DefaultCrudTable'
import { columns } from './handlers'
import CustomTableBody from './components/CustomTableBody'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button, useToast } from '@chakra-ui/react'
import {
  retryAssetLicensesBatch,
  retryAssetLicensesBatchItem
} from '@/services/assets-licenses-batches'
import RetryModal from './components/RetryModal'
import { RETRY_BATCH } from '@/configs/strings/batches'
import { deleteAIMetadata, updateAIMetadata } from '@/services/ai-metadata'
import { setErrorCodeAndMessage } from '@/utils/fetch'
import {
  retryAIMetadataBuildBatchProcessing,
  retryAIMetadataBuildItemProcessing
} from '@/services/ai-metadata-build-batches'
import { AI_METADATA } from '@/configs/strings/ai-metadata.strings'
import { defaultToastConfig } from '@/configs/toast'

const AIMetadataDataLists = ({
  batchHasErrors,
  batchId,
  batchName,
  isHiddenForm,
  onChangePage,
  page,
  totalPages,
  assetResponse,
  reloadItems,
  aiStrategies
}) => {
  const [retryModalIsOpen, setRetryModalIsOpen] = useState(false)
  const [retryProcess, setRetryProcess] = useState()
  const [retryBatch, setRetryBatch] = useState()
  const [currentData, setCurrentData] = useState()
  const [assets, setAssets] = useState([])
  const toast = useToast()

  const { getAccessTokenSilently: getAccessToken } = useAuth0()

  React.useEffect(() => {
    if (assetResponse?.data) {
      setAssets(assetResponse?.data)
    }
  }, [assetResponse])

  const onOpenRetryBatchModal = () => {
    setRetryProcess(() => retryAIMetadataBuildBatchProcessing)
    setRetryModalIsOpen(true)
    setRetryBatch(true)
  }

  const onOpenRetryModal = (licenseData) => {
    setRetryProcess(() => retryAIMetadataBuildItemProcessing)
    setCurrentData(licenseData)
    setRetryModalIsOpen(true)
  }

  const onCloseRetryModal = () => {
    reloadItems()
    setRetryModalIsOpen(false)
    setCurrentData(null)
    setRetryBatch(false)
  }

  const handleEdit = async (updatedItem) => {
    try {
      await updateAIMetadata(
        updatedItem.id,
        {
          preDealAIStrategies: updatedItem.preDealAIStrategies,
          separateIngredients: updatedItem.separateIngredients
        },
        getAccessToken
      )

      const updatedAssets = assets.map(item =>
        item.id === updatedItem.id ? updatedItem : item
      )

      reloadItems && reloadItems()

      setAssets(updatedAssets)

      toast({
        title: `${AI_METADATA} updated`,
        description: `The ${AI_METADATA} has been successfully updated.`,
        status: 'success',
        ...defaultToastConfig
      })
    } catch (error) {
      const currentError = setErrorCodeAndMessage(error)
      toast({
        title: 'Update failed',
        description: currentError.code,
        status: 'error',
        ...defaultToastConfig
      })
    }
  }

  const handleDelete = async (item) => {
    try {
      await deleteAIMetadata(item.id, getAccessToken)

      const updatedAssets = assets.filter(asset => asset.id !== item.id)

      setAssets(updatedAssets)

      toast({
        title: `${AI_METADATA} deleted`,
        description: `The ${AI_METADATA} has been successfully deleted.`,
        status: 'success',
        duration: 3000,
        isClosable: true
      })
    } catch (error) {
      const currentError = setErrorCodeAndMessage(error)

      toast({
        title: 'Delete failed',
        description: currentError.code,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    }
  }

  return (
    <>
      {retryModalIsOpen &&
        <RetryModal
          aiMetadata={currentData}
          batchId={batchId}
          batchName={batchName}
          getAccessToken={getAccessToken}
          onClose={onCloseRetryModal}
          onRetry={retryProcess}
          retryBatch={retryBatch}
          toast={toast}
        />
      }
      <section>
        { batchHasErrors &&
          <div className="mt-4 mb-4">
            <Button
              onClick={onOpenRetryBatchModal}
            >
              { RETRY_BATCH }
            </Button>
          </div>
        }
        { assets &&
          <Box>
            <DefaultCrudTable
              page={page}
              totalPages={totalPages}
              onPageChange={(page) => onChangePage(page)}
            >
              <DefaultCrudTable.Header columns={columns} />
              <CustomTableBody
                columns={columns}
                items={assets}
                getAccessToken={getAccessToken}
                isHiddenForm={isHiddenForm}
                onRetry={onOpenRetryModal}
                onEdit={handleEdit}
                onDelete={handleDelete}
                aiStrategies={aiStrategies}
              />
            </DefaultCrudTable>
          </Box>
        }
      </section>
    </>
  )
}

export default AIMetadataDataLists
